// cleanup_class_to_function_SECURITY_INFORMATION
// cleanup_hoc_to_hooks_SECURITY_INFORMATION

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import jsCookie from 'js-cookie';
import { zip } from 'rxjs';

import Alert from '@glu/alerts-react';
import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';

import ChallengeManager from 'components/challengeManager/ChallengeManager';
import Loader from 'components/loader/Loader';
import SecurityContact from 'components/securityContact/SecurityContact';
import {
  contactTypes,
  FALLBACK_VRU_DELAY,
  INPUT_TYPES,
  VIEW_TYPES
} from 'components/securityContact/securityContacts.constants';
import withLocale from 'components/withLocale/withLocale';

import { UserShape, UserDefaultProps } from 'api/user/props';
import { fetchUserSecurityInfo, updatesSecurityContactInfo } from 'api/user/service';

import { ALERTVARIATIONS, API_ERROR, HEADERS, SUBMIT } from 'constants.js';
import { unsubscribeComponent } from 'utils/unsubscribeComponent';

export class SecurityInformationComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      challengeErrorMessage: false,
      challengeManager: false,
      data: null,
      default: '',
      loaded: false,
      modalOpen: false,
      securityInformation: [
        {
          contactType: contactTypes.sms,
          inputType: INPUT_TYPES.PHONE,
          value: ''
        },
        {
          contactType: contactTypes.email,
          inputType: INPUT_TYPES.EMAIL,
          value: this.props.user?.userInformation?.email || ''
        },
        {
          contactType: contactTypes.voice,
          inputType: INPUT_TYPES.PHONE,
          value: ''
        }
      ],
      sending: false
    };
  }

  componentDidMount() {
    if (!this.props.getPreviousData) {
      return this.handleUserData({ success: true });
    }

    return this.loadUserData();
  }

  loadUserData() {
    this.observables.fetchUserSecurityInfo$ = fetchUserSecurityInfo(this.props.viewType);
    this.subscriptions.fetchUserSecurityInfo = this.observables.fetchUserSecurityInfo$.subscribe(
      this.handleUserData
    );

    return zip(Object.values(this.observables));
  }

  componentWillUnmount() {
    unsubscribeComponent(this.subscriptions);
  }

  observables = {
    fetchUserSecurityInfo$: null,
    updatesSecurityContactInfo$: null
  };

  subscriptions = {
    fetchUserSecurityInfo: null,
    updatesSecurityContactInfo: null
  };

  handleUserData = ({ success, data: securityInfo = [] } = {}) => {
    const { locale } = this.props;
    const { securityInformation: defaultInformation } = this.state;

    if (!success || !Array.isArray(securityInfo)) {
      return this.setState({
        errorMessage: locale['common.server.error'],
        loaded: true,
        successMessage: ''
      });
    }

    const defaultedInfo = securityInfo.find(({ default: defaulted }) => defaulted) || {};
    const defaultInvalid =
      (defaultedInfo.contactType === contactTypes.voice && !this.props?.user?.voiceEnabled) ||
      (defaultedInfo.contactType === contactTypes.email && this.props?.user?.isEmailOTPDisallowed);

    const securityInformation = defaultInformation.map((defaultContact) => {
      const contact =
        securityInfo.find(({ contactType }) => contactType === defaultContact.contactType) ||
        defaultContact;
      const { contactType } = contact;
      const localeName = contactType.toLowerCase();
      const fallbackValue =
        contactType === contactTypes.sms ? this.props.user?.userInformation?.phoneNumber : '';

      return {
        ...defaultContact,
        ...contact,
        default: contact === defaultedInfo && !defaultInvalid,
        errorMessage: '',
        helpText: locale[`profile.helpText.${localeName}`],
        label: locale[`profile.label.${localeName}`],
        value: contact.value || fallbackValue || '',
        verified: !!contact.value && !defaultInvalid,
        defaultInvalid
      };
    });

    return this.setState({
      errorMessage: defaultInvalid ? locale['profile.noDefaultError'] : '',
      informationOrigin: securityInformation,
      loaded: true,
      securityInformation
    });
  };

  saveContacts = (event) => {
    if (event) {
      event.preventDefault();
    }
    this.setState({ sending: true });
    const { viewType } = this.props;
    const { informationOrigin, securityInformation } = this.state;
    const securityContacts = securityInformation
      .map((contact, index) => {
        const verifiedSMS = contact.verified || contact.contactType !== contactTypes.sms;
        const verified = verifiedSMS && !contact.errorMessage;
        const info = verified || !contact.value ? contact : informationOrigin[index];
        const value = info.value;
        const extension = info.extension ? info.extension : null;
        const vruDelay = info.vruDelay && info.extension ? info.vruDelay : FALLBACK_VRU_DELAY;

        return {
          contactType: info.contactType,
          default: info.default,
          extension,
          value,
          vruDelay
        };
      })
      .filter(
        ({ contactType, value }) =>
          value &&
          contactType &&
          (!this.props?.user?.isEmailOTPDisallowed || contactType !== contactTypes.email)
      );

    const obs = this.observables;
    obs.updatesSecurityContactInfo$ = updatesSecurityContactInfo(securityContacts, viewType);
    this.subscriptions.updatesSecurityContactInfo = obs.updatesSecurityContactInfo$.subscribe(
      this.handleResponse,
      this.handleResponse
    );
    return false;
  };

  handleResponse = ({ data, success }) => {
    const { locale, onSuccess } = this.props;
    this.setState({ sending: false });
    jsCookie.remove(HEADERS.X_CM_PASSCODE);
    jsCookie.remove(HEADERS.X_NEXTCM_PASSCODE);

    if (data?.challeangeable || data?.[0]?.code === API_ERROR.ACCOUNT_LOCKED) {
      return this.setState({
        challengeErrorMessage: !!data.numberOfFailedAttempts,
        challengeManager: true,
        data,
        modalOpen: true
      });
    }

    if (!success) {
      return this.handleError(data);
    }

    this.loadUserData();

    if (onSuccess) {
      return onSuccess(data);
    }

    return this.setState({
      challengeErrorMessage: false,
      challengeManager: false,
      errorMessage: '',
      sending: false,
      successMessage: locale['profile.updated']
    });
  };

  resetError = () => {
    this.setState({
      errorMessage: ''
    });
  };

  handleError = (data) => {
    const { locale } = this.props;
    const [{ code, userMessage } = {}] = data || [{}];
    this.setState({
      errorMessage: locale[code] || userMessage || locale['common.server.error'],
      sending: false,
      successMessage: ''
    });
  };

  reset = ({ contactType, target }) => {
    const { informationOrigin, securityInformation: securityInfo } = this.state;
    if (target || !contactType) {
      return this.componentDidMount();
    }

    const securityInformation = securityInfo.map((contact, index) => {
      if (contactType === contact.contactType) {
        return informationOrigin[index];
      }
      return contact;
    });

    return this.setState({ securityInformation });
  };

  handleChange = (changedContact) => {
    const { securityInformation: securityInfo } = this.state;
    const securityInformation = securityInfo.map((contact) => {
      const information =
        contact.contactType === changedContact.contactType ? changedContact : contact;

      return {
        ...contact,
        ...information,
        default: changedContact.default
          ? contact.contactType === changedContact.contactType
          : contact.default
      };
    });

    this.setState({ securityInformation });
  };

  closeModal = () => {
    this.setState({
      challengeManager: false,
      modalOpen: false
    });
  };

  render() {
    const { actions, locale, onCancel } = this.props;

    const {
      challengeErrorMessage,
      challengeManager,
      data,
      errorMessage,
      loaded,
      modalOpen,
      securityInformation,
      sending,
      successMessage
    } = this.state;

    const hasError = securityInformation.some(({ contactType, errorMessage, value, verified }) => {
      const unverifiedSMS = !verified && contactType === contactTypes.sms;
      const unverifiedValue = value && unverifiedSMS;
      return errorMessage || unverifiedValue;
    });

    const hasValue = securityInformation.some(({ value }) => value);
    const hasDefault = securityInformation.some(
      ({ default: defaulted, value }) => value && defaulted
    );

    const hasErrors = hasError || !hasValue || !hasDefault;
    if (challengeManager) {
      return (
        <ChallengeManager
          onCancel={this.closeModal}
          onHide={this.closeModal}
          data={data}
          onSubmit={this.saveContacts}
          challengeErrorMessage={challengeErrorMessage}
          open={modalOpen}
        />
      );
    }

    return (
      <form onSubmit={this.saveContacts} data-qa="security-contact-form">
        <div className="form-group col-xs-12">
          <h2 data-qa="security-contact-form-header">{locale['profile.securityInformation']}</h2>
          <p data-qa="security-contact-form-sub-header">{locale['profile.security.subheader']}</p>
        </div>

        {!loaded && <Loader />}

        <div className="col-xs-12 col-sm-8">
          {errorMessage && (
            <Alert
              variant={ALERTVARIATIONS.WARNING}
              id="securityInformationError"
              onHide={() => this.setState({ errorMessage: '' })}
              dataQa="warning-alert"
            >
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert
              variant={ALERTVARIATIONS.SUCCESS}
              onHide={() => this.setState({ successMessage: '' })}
              dataQa="success-alert"
            >
              {successMessage}
            </Alert>
          )}
        </div>

        {loaded && (
          <section>
            {securityInformation.map((contact) => {
              const condition =
                contact.contactType === contactTypes.sms ||
                (contact.contactType === contactTypes.voice && this.props?.user?.voiceEnabled) ||
                (contact.contactType === contactTypes.email &&
                  !this.props?.user?.isEmailOTPDisallowed);
              return condition ? (
                <SecurityContact
                  contact={contact}
                  disabled={sending}
                  key={contact.contactType}
                  onChange={this.handleChange}
                  onReset={this.reset}
                  sending={sending}
                  handleError={this.handleError}
                  resetError={this.resetError}
                />
              ) : null;
            })}
            <article className="form-group col-xs-12">
              {!actions && (
                <Button
                  variant={PRIMARY}
                  className="btn btn-primary"
                  dataQa="submit-button"
                  disabled={sending || hasErrors}
                  type={SUBMIT}
                >
                  {locale['common.save']}
                </Button>
              )}
              {onCancel && (
                <Button
                  variant={TERTIARY}
                  className="btn btn-tertiary"
                  dataQa="cancel-button"
                  disabled={sending}
                  onClick={onCancel}
                >
                  {locale['common.cancel']}
                </Button>
              )}
              {actions && actions({ ...this.state, hasErrors })}
            </article>
          </section>
        )}
      </form>
    );
  }
}

SecurityInformationComponent.propTypes = {
  actions: PropTypes.func,
  getPreviousData: PropTypes.bool,
  locale: PropTypes.objectOf(PropTypes.string),
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  user: UserShape,
  viewType: PropTypes.oneOf(Object.values(VIEW_TYPES))
};

SecurityInformationComponent.defaultProps = {
  actions: null,
  getPreviousData: true,
  locale: {},
  onCancel: null,
  onSuccess: null,
  user: UserDefaultProps,
  viewType: VIEW_TYPES.MAINTENANCE
};

/* istanbul ignore next */
function mapStateToProps(state) {
  return {
    user: state.auth.user
  };
}

const ConnectSecurityInformation = connect(mapStateToProps)(SecurityInformationComponent);
export default withLocale(ConnectSecurityInformation);
